import { cva } from 'class-variance-authority';

/**
 * Menu class recipes — the action menu ("more actions"), not the select's option list.
 *
 * Zero `dark:` variants, as everywhere else in this layer: colour comes entirely from the
 * `--ds-menu-*` slots, which `.ds-menu` sets per theme in `components/styles/design-system.css`.
 * Read the block header there first — three of those slots are EXTRAPOLATED rather than authored,
 * because the drawn `Menu` component is a selection list with no divider and no destructive row.
 *
 * Geometry is measured off node 556:20625 and matches the select's list exactly, which is expected:
 * Figma draws one `Menu` component and both surfaces instance it.
 */

/**
 * The panel.
 *
 * `ds-menu` is applied by the component rather than inherited, for the same reason `Select` applies
 * `ds-select`: the popup is portalled into `<body>`, so it has none of the trigger's ancestors and
 * would otherwise resolve every slot to its light-mode fallback. The theme class goes on the
 * `Portal` instead, which IS an ancestor of this element, so `:where(.dark) .ds-menu` still reaches
 * it.
 *
 * Width is content-driven with a 220px floor, and this is the one place the action menu must NOT
 * copy the select. The select pins its panel to `--anchor-width + 2px` so the list lines up with
 * the field it belongs to; an action menu's anchor is a 40px icon button, so borrowing that rule
 * would render a 40px-wide panel. 220px is the row width the component draws (node 556:20713), used
 * here as a minimum rather than a fixed size so a long label sets the width instead of truncating.
 *
 * `max-h-[--available-height]` plus scrolling keeps a long menu on screen near the bottom of the
 * viewport; Base UI computes the value from the collision boundary.
 */
export const menuPanelRecipe = cva([
  'ds-menu',
  'box-border flex w-max min-w-[220px] flex-col',
  'max-h-[var(--available-height)] overflow-y-auto overscroll-contain',
  'px-[12px] py-[16px]',
  'rounded-[20px]',
  'border-solid border-[length:1px] border-[var(--ds-menu-border)]',
  'bg-[var(--ds-menu-bg)]',
  'outline-none',
]);

/**
 * The positioner.
 *
 * Only a stacking context — all the geometry is inline styles from Base UI's anchor positioning.
 * The z-index belongs HERE rather than on the panel, because this is the element Base UI gives
 * `position: fixed`; on the panel it would have nothing to stack against.
 *
 * `z-[1400]` sits above MUI's modal (1300) and drawer (1200) and below its tooltip (1500) — the
 * same value and the same reasoning as `selectPositionerRecipe`. An action menu inside a legacy MUI
 * `Dialog` is an ordinary case during the refresh, and a popup trapped behind its own dialog is
 * unusable. The value is MUI's scale rather than this layer's because MUI is what it coexists with.
 */
export const menuPositionerRecipe = cva(['z-[1400]']);

/**
 * A row.
 *
 * 13px Medium on a 20px line with 6px of inset is the design's 32px row (Body XS Med) — the same
 * cell the select's options use.
 *
 * `data-highlighted` covers BOTH mouse hover and keyboard navigation, because Base UI highlights on
 * hover by default. That is what the design wants: it draws one hover treatment and no separate
 * keyboard-focus row, so pointer and keyboard users see the same thing and there is no second state
 * to keep in step.
 *
 * There is deliberately no selected state. The drawn component has one — it is a selection list —
 * but an action menu has nothing to select: each row runs a command and the panel closes. That is
 * also why `Select`'s trailing indicator has no counterpart here.
 *
 * Selector precedence is kept mutually exclusive rather than order-dependent, as in the sibling
 * recipes: disabled wins over hovered by excluding it, so which rule applies never depends on
 * utility sort order.
 */
export const menuItemRecipe = cva(
  [
    'group/mi',
    'relative flex w-full shrink-0 items-center gap-[8px]',
    'm-0 appearance-none border-0 bg-transparent text-left',
    'px-[12px] py-[6px]',
    'rounded-[24px]',
    'font-sans text-[13px] leading-5 font-medium select-none',
    'outline-none',
    'cursor-pointer',
    'transition-[color,background-color] duration-150 motion-reduce:transition-none',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-primary',

    /**
     * No disabled cell is drawn for this component, so the treatment follows the select's rather
     * than being invented separately — `opacity-50` over the row's own colours. Documented because
     * the legacy menu DOES support disabled rows, so this is reachable.
     */
    'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50',
  ],
  {
    variants: {
      tone: {
        default: [
          'text-[var(--ds-menu-item-fg)]',
          'data-[highlighted]:not-data-[disabled]:bg-[var(--ds-menu-item-bg-hover)]',
          'data-[highlighted]:not-data-[disabled]:text-[var(--ds-menu-item-fg-hover)]',
        ],
        /**
         * The destructive row keeps ONE label colour across resting and hovered, unlike a default
         * row's one-step brightening. That is deliberate: `.ds-btn-destructive-outline` also holds
         * `--ds-btn-fg` and `--ds-btn-fg-hover` at the same value, so red does not shift on hover
         * anywhere else in the system either. The fill is what changes.
         */
        destructive: [
          'text-[var(--ds-menu-item-fg-destructive)]',
          'data-[highlighted]:not-data-[disabled]:bg-[var(--ds-menu-item-bg-destructive-hover)]',
        ],
      },
    },
    defaultVariants: { tone: 'default' },
  }
);

/**
 * A row's leading glyph. Sized and boxed exactly as the select's option icons, so a 16px icon lands
 * the same way in both menus.
 *
 * Takes the row's colour through `currentColor` rather than a slot of its own — which is what makes
 * the glyph on a destructive row red without the caller knowing anything about tones. The select's
 * icons do the same.
 */
export const menuItemIconRecipe = cva([
  'inline-flex size-[16px] shrink-0 items-center justify-center',
  '[&_svg]:size-full',
]);

/** The label. `min-w-0` releases the flex item's minimum content size so `truncate` can shrink it. */
export const menuItemLabelRecipe = cva(['min-w-0 truncate']);

/**
 * The column holding the label and, when there is one, the description.
 *
 * Always rendered, even for a single-line row, so the label's box is the same element in both cases
 * and a row cannot pick up different flex behaviour by gaining a description. On a row without one
 * the column collapses to the label's own line box, which is what the 32px cell already was.
 */
export const menuItemTextRecipe = cva(['flex min-w-0 flex-1 flex-col']);

/**
 * The description.
 *
 * EXTRAPOLATED — see `MenuOption.description`. Colour comes from the row through inheritance rather
 * than from a slot of its own, following `Alert`, where the title and the description are both
 * `--ds-alert-fg`. 12px Regular on a 20px line is one step down from the label's 13px Medium, the
 * same one-step relationship the alert draws between its own two lines.
 *
 * `max-w-[280px]` is what makes the line WRAP. The panel is `w-max`, so without a concrete width
 * the description's max-content width would simply widen the panel until the whole sentence fit on
 * one line — a ~70-character line producing a ~450px popover. 280px is a chosen number, not a
 * measured one: the drawn row is 220px (node 556:20713), and this is the next step up that reads as
 * a menu rather than a dialog. It applies here rather than on the panel so a menu WITHOUT
 * descriptions keeps sizing to its content exactly as before.
 */
export const menuItemDescriptionRecipe = cva(['text-[12px] leading-5 font-normal', 'max-w-[280px] whitespace-normal']);

/**
 * The group separator.
 *
 * EXTRAPOLATED — see the `.ds-menu` block header. Rendered as a bordered `<div>` rather than an
 * `<hr>`: Base UI's `Menu.Separator` sets `role="separator"` itself, and a UA-styled `<hr>` would
 * bring its own margins into a panel whose spacing is already specified.
 *
 * The negative inset is what makes the line span the full panel rather than stopping inside the
 * rows' 12px padding — the legacy menu uses MUI's `variant="middle"` Divider, which insets it. The
 * design draws no separator at all, so this follows the panel's own border, which runs edge to edge.
 * `my-[8px]` is half a row's height, chosen so a group break reads as lighter than a row gap.
 */
export const menuSeparatorRecipe = cva([
  '-mx-[12px] my-[8px] h-0 shrink-0',
  'border-0 border-t border-solid border-[var(--ds-menu-separator)]',
]);
